import React, { useState, useRef } from 'react';
import Autosuggest, { InputProps } from 'react-autosuggest';
import { searchProfiles } from '@/services/profiles';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import style from './Autocomplete.module.scss';
import { Profile, TranslatableText } from '@/types';
import { ProfileIcon } from '@/components/Icons';

type OwnProps = Omit<InputProps<Profile>, 'onChange' | 'value'> & {
    onChange(suggestion: Profile): void;
    fieldName?: TranslatableText;
};

const renderSuggestion = (suggestion: Profile) => (
    <div className={style.suggestionContent}>
        <div className={style.suggestionAvatar}>
            <ProfileIcon profileId={suggestion.id} />
        </div>
        <div className={style.suggestionText}>
            <div>{suggestion.name}</div>
        </div>
    </div>
);

const ProfileAutocomplete: React.FC<OwnProps> = ({ onChange, fieldName, required, ...rest }) => {
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<Profile[]>([]);
    const activeRequest = useRef<Promise<Profile[]> | null>(null);

    const onSuggestionsFetchRequested = ({ value }) => {
        activeRequest.current = searchProfiles({ text: value });

        activeRequest.current.then((result) => {
            setSuggestions(result);
        });
    };

    const onSuggestionSelected = (event, { suggestion, suggestionValue, suggestionIndex, sectionIndex, method }) => {
        triggerHandler(onChange, suggestion);
    };

    const inputProps: InputProps<Profile> = {
        ...rest,
        placeholder: 'Start typing...',
        value,
        onChange: (event, { newValue }) => setValue(newValue),
    };

    return (
        <Autosuggest
            theme={style}
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={() => setSuggestions([])}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={(suggestion) => suggestion.name}
            renderSuggestion={renderSuggestion}
            inputProps={inputProps}
        />
    );
};

export default ProfileAutocomplete;